import { FC, Fragment, useEffect, useState } from 'react';
import { PackageDetails } from 'ts/package/interfaces/PackageDetails';
import { useTranslation } from 'react-i18next';
import { formatPrice } from 'utils/utils';
import { getCurrency, formatPriceCurrency } from 'utils/utils';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { Select } from 'ts/interfaces/Generic';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import Button from 'components/ui/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowCircleDown, faArrowCircleUp } from '@fortawesome/free-solid-svg-icons';
import { formatOccupancySrt } from 'utils/package';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { RoomTour } from 'ts/package/interfaces/Package';
import { isTT } from 'constants/generic';

interface RoomsDetailsProps {
  dealModel: PackageDetails;
  onChangePax: (totalPrice: number, totalAvgPrice: number) => void;
  onClickBook: () => void;
  availableRooms: RoomTour[];
  setAvailableRooms: (rooms: RoomTour[]) => void;
  infants: number;
  setInfants: (infants: number) => void;
}

const RoomsDetails: FC<RoomsDetailsProps> = ({
  dealModel: { deal, description },
  onChangePax,
  availableRooms,
  setAvailableRooms,
  onClickBook,
  infants,
  setInfants,
}) => {
  const [t] = useTranslation();
  const infantPrice = description.nC_InfantPrice.price;

  const [open, setOpen] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalAvgPrice, setTotalAvgPrice] = useState(0);
  const [infantOptions, setInfantOptions] = useState<Select[]>([]);

  useEffect(() => {
    if (description.ncCompItems.length) {
      const rooms = description.ncCompItems
        .map((item) => {
          return {
            code: item.roomcode,
            roomType: item.roomType,
            price: item.salePrice,
            currency: item.currency,
            occupancyStr: formatOccupancySrt(item.roomcode),
            paxes: item.roomcode.length,
            checked: false,
            visible: item.roomcode === deal.paxComplect,
            bookCode: item.bookCode,
          };
        })
        .sort((a, b) => (a.visible === b.visible ? 0 : a.visible ? -1 : 1));

      rooms[0].checked = true;
      onChangePax(rooms[0].price, rooms[0].price / rooms[0].paxes);
      setTotalPrice(rooms[0].price);
      setTotalAvgPrice(rooms[0].price / rooms[0].paxes);

      if (infantPrice) {
        setInfantOptions([
          { value: 0, label: '0' },
          { value: 1, label: `1 - ${formatPrice(infantPrice, deal.currency)}` },
          { value: 2, label: `2 - ${formatPrice(infantPrice * 2, deal.currency)}` },
        ]);
      }

      setAvailableRooms(rooms);
    }
  }, []); //  eslint-disable-line react-hooks/exhaustive-deps

  const onChangeInfants = (value: number) => {
    const { newTotalPrice, newTotalAvgPrice } = getTotals(availableRooms, value);

    setInfants(value);
    onChangePax(newTotalPrice, newTotalAvgPrice);
    setTotalPrice(newTotalPrice);
    setTotalAvgPrice(newTotalAvgPrice);
  };

  const onSelectRooms = (room: RoomTour, index: number) => {
    const newRooms = availableRooms.map((item) => ({ ...item, checked: false }));

    newRooms[index] = room;

    const { newTotalPrice, newTotalAvgPrice } = getTotals(newRooms, infants);

    setAvailableRooms(newRooms);
    setTotalPrice(newTotalPrice);
    setTotalAvgPrice(newTotalAvgPrice);
    onChangePax(newTotalPrice, newTotalAvgPrice);
  };

  const getTotals = (newRooms: RoomTour[], newInfants: number) => {
    const room = newRooms.find((room) => room.checked);
    if (room) {
      const newTotalPrice = room.price + newInfants * infantPrice;
      const newTotalAvgPrice = newTotalPrice / room.paxes;

      return { newTotalPrice, newTotalAvgPrice };
    }

    return { newTotalPrice: 0, newTotalAvgPrice: 0 };
  };

  const onClickOpen = () => {
    setOpen(!open);

    const newRooms = [...availableRooms];
    if (!open) {
      newRooms.forEach((room) => {
        room.visible = true;
      });
    } else {
      newRooms.forEach((room, index) => {
        room.visible = room.code === deal.paxComplect || index === 0;
      });
    }
    setAvailableRooms(newRooms);
  };

  return (
    <Fragment>
      <div className="default-shadow flight-details-item">
        <div className="table-default table-high mb-20 mt-20 table-mobile">
          <table className="table-bordered-lines">
            <thead>
              <tr>
                <th>{t('hotel.paxes')}</th>
                <th>{t('hotel.room_type')}</th>
                <th>{t('notes')}</th>
                <th>{t('price')}</th>
              </tr>
            </thead>
            <tbody>
              {availableRooms
                .filter((item) => item.visible)
                .map((item, index) => {
                  return (
                    <tr key={index}>
                      <td className="text-sm">
                        <Checkbox
                          onlyChecked={true}
                          checked={item.checked}
                          onChange={(checked) => onSelectRooms({ ...item, checked }, index)}
                          htmlLabel={<span className="text-bold">{item.occupancyStr}</span>}
                        />
                      </td>
                      <td className="text-sm">
                        <div className="text-cap mb-3">{item.roomType?.toLowerCase()}</div>
                      </td>
                      <td>{}</td>
                      <td className=" text-bold text-contrast">
                        {formatPriceCurrency(item.price, getCurrency(item.currency))}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <div className="flex-between ph-10">
          <div>
            {!!infantOptions.length && (
              <div className="flex-center mb-20 text-sm">
                <span className="w200px mb-10">
                  {t('search.infants')} ({t('search.under')} 2){' '}
                </span>
                <div className="w200px">
                  <SelectCustom
                    options={infantOptions}
                    value={infants}
                    onChange={onChangeInfants}
                  />
                </div>
              </div>
            )}
          </div>

          {availableRooms.length > 1 && (
            <div className="flex-row-end mb-20 text-sm cur-poi" onClick={onClickOpen}>
              <FontAwesomeIcon
                icon={open ? faArrowCircleUp : faArrowCircleDown}
                className="mr-5 text-contrast"
              />
              {t('hotel.more_room_types', {
                count: availableRooms.filter((item) => item.code !== deal.paxComplect).length,
              })}
            </div>
          )}
        </div>

        {!!totalAvgPrice && !!totalPrice && (
          <Fragment>
            <div className="text-st text-contrast text-bold mb-5">
              {t('flight.total_average_passenger')}:{' '}
              {formatPriceCurrency(totalAvgPrice, getCurrency(deal.currency))}
            </div>
            <div className="text-xsm text-light">
              {t('total')}: {formatPriceCurrency(totalPrice, getCurrency(deal.currency))}
            </div>
          </Fragment>
        )}
      </div>
      <div className="flex-row-end">
        <Button
          className="btn btn-primary btn-lg"
          title={t(isTT ? 'continue' : 'hotel.book')}
          onClick={onClickBook}
        />
      </div>
    </Fragment>
  );
};

export default withErrorBoundary(RoomsDetails, 'RoomsDetails');
